/* eslint-disable @typescript-eslint/no-explicit-any */
import apiClient, { IresponseData, IresponseDataPaginated } from "@/lib/api";
import { Client } from "./client";
import { Subscription, EownerType } from './subscription';

// Enums
export enum EPaymentMethod {
  CASH = "CASH",
  TRANSFER = "TRANSFER",
  CARD = "CARD",
  NEQUI = "NEQUI",
  DAVIPLATA = "DAVIPLATA",
  BANCOLOMBIA = "BANCOLOMBIA",
  PAYPAL = "PAYPAL",
  BINANCE = "BINANCE",
  YAPE = "YAPE",
}

export enum EPaymentStatus {
  PENDING = "PENDING",
  COMPLETED = "COMPLETED",
  CANCELLED = "CANCELLED",
  REFUNDED = "REFUNDED",
}

export interface PaymentSupplierInfo {
  id: number;
  name: string;
}

export interface Payment {
  id: string;
  accountCode: string;
  subscriptionId: string;
  clientId: string;
  supplierId?: number;
  ownerType?: EownerType;
  amount: number;
  paymentDate: string;
  paymentMethod: EPaymentMethod;
  status: EPaymentStatus;
  notes?: string;
  client: Client;
  supplier?: PaymentSupplierInfo;
  subscription?: Subscription
  createdAt: string;
  updatedAt?: string;
}

// Request DTOs
export interface CreatePaymentRequest {
  subscriptionId: string;
  amount: number;
  paymentDate: string;
  paymentMethod: EPaymentMethod;
  status?: EPaymentStatus;
  notes?: string;
  ownerType?: EownerType;
  supplierId?: number;
}

export interface UpdatePaymentRequest {
  amount?: number;
  paymentDate?: string;
  paymentMethod?: EPaymentMethod;
  status?: EPaymentStatus;
  notes?: string;
}

// Filter params
export interface PaymentFilters {
  clientId?: string;
  subscriptionId?: string;
  paymentMethod?: EPaymentMethod;
  supplierId?: number;
  ownerType?: EownerType;
  status?: EPaymentStatus;
  page?: number;
  limit?: number;
}

/**
 * Elimina campos vacíos de un objeto
 */
function removeEmptyFields<T extends Record<string, any>>(obj: T): Partial<T> {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (value !== "" && value !== null && value !== undefined) {
      acc[key] = value;
    }
    return acc;
  }, {} as any);
}

/**
 * Servicio para gestionar pagos
 */
export const paymentService = {
  /**
   * Registrar un nuevo pago
   */
  async create(data: CreatePaymentRequest): Promise<IresponseData<Payment>> {
    try {
      const cleanData = removeEmptyFields(data);
      const response = await apiClient.post<IresponseData<Payment>>(
        "/payments",
        cleanData,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al registrar el pago. Por favor, intenta de nuevo.",
      );
    }
  },

  /**
   * Obtener todos los pagos
   */
  async getAll(
    filters?: PaymentFilters,
  ): Promise<IresponseDataPaginated<Payment[]>> {
    try {
      const cleanFilters = filters ? removeEmptyFields(filters) : {};
      const response = await apiClient.get<IresponseDataPaginated<Payment[]>>(
        "/payments",
        { params: cleanFilters },
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al obtener los pagos. Por favor, intenta de nuevo.",
      );
    }
  },

  /**
   * Obtener un pago por ID
   */
  async getOne(id: string): Promise<IresponseData<Payment>> {
    try {
      const response = await apiClient.get<IresponseData<Payment>>(
        `/payments/${id}`,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al obtener el pago. Por favor, intenta de nuevo.",
      );
    }
  },

  /**
   * Actualizar un pago
   */
  async update(
    id: string,
    data: UpdatePaymentRequest,
  ): Promise<IresponseData<Payment>> {
    try {
      const cleanData = removeEmptyFields(data);
      if (cleanData.amount) {
        cleanData.amount = Number(cleanData.amount);
      }
      const response = await apiClient.put<IresponseData<Payment>>(
        `/payments/${id}`,
        cleanData,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al actualizar el pago. Por favor, intenta de nuevo.",
      );
    }
  },
};
